import { useCurrentUserSuspenseQuery, useUserSetContactDetailsMutation } from '../../graphql/react-operations';
import { Trans } from '@lingui/react';
import { Box } from '@startuptools/ui/Box';
import { Typography } from '@startuptools/ui/Typography';
import { Input } from '@startuptools/ui/Input';
import { Stack } from '@startuptools/ui/Stack';
import { Gravatar } from '@startuptools/ui/Gravatar';
import { Button } from '@startuptools/ui/Button';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { RadioGroup } from '@startuptools/ui/RadioGroup';
import { Radio } from '@startuptools/ui/Radio';
import { GenericError } from '@startuptools/ui/Error';
import { type GqlUserSetContactDetailsInput, GqlReferralSource } from '../../graphql/base-types.graphql';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { type ComponentProps, Suspense, useState } from 'react';
import { captureException } from '@sentry/browser';
import { Grid } from '@startuptools/ui/Grid';
import { Card } from '@startuptools/ui/Card';

const REFERRAL_SOURCES = [
  { value: GqlReferralSource.Startuptools, label: <Trans id="Via StartupTools" /> },
  { value: GqlReferralSource.Google, label: <Trans id="Google" /> },
  { value: GqlReferralSource.Linkedin, label: <Trans id="LinkedIn" /> },
  { value: GqlReferralSource.Friend, label: <Trans id="Tips from a friend/colleague" /> },
  { value: GqlReferralSource.Marketing, label: <Trans id="Marketing email" /> },
  { value: GqlReferralSource.Event, label: <Trans id="Company event" /> },
  { value: GqlReferralSource.Bolago, label: <Trans id="Contacted by Bolago" /> },
  { value: GqlReferralSource.Other, label: <Trans id="Other" /> },
] as const;

export const UserProfileEditInner = ({
  onClose,
  showReferralFields = false,
  title,
}: {
  onClose: () => void;
  showReferralFields?: boolean;
  title?: React.ReactNode;
}) => {
  const [setUserDetails, { loading: userDetailsLoading }] = useUserSetContactDetailsMutation();
  const [showError, setShowError] = useState(false);
  const {
    data: { currentUser: user },
  } = useCurrentUserSuspenseQuery();
  const { register, handleSubmit, watch, control } = useForm<GqlUserSetContactDetailsInput>({
    defaultValues: {
      name: user.name ?? '',
      email: user.email ?? '',
      phone: user.phone,
      communicationLanguage: user.communicationLanguage,
      referralSource: user.referralSource,
      referralSourceOther: user.referralSourceOther,
    },
  });

  const referralSource = watch('referralSource');

  const onSubmit: SubmitHandler<GqlUserSetContactDetailsInput> = async input => {
    if (showError) {
      setShowError(false);
    }
    try {
      await setUserDetails({ variables: { input } });
      onClose();
    } catch (error) {
      setShowError(true);
      captureException(error);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} margin={2}>
      <Grid container spacing={4}>
        <Grid xs={12}>{title}</Grid>

        <Grid xs={12} md={5}>
          <Stack direction="row" gap={2} alignItems="center">
            <Gravatar md5Hash={user?.emailMd5} size="lg" uuid={user?.id}>
              {user.name?.[0] || user.email?.[0]}
            </Gravatar>
            <Typography level="h4">{user.name || user.email}</Typography>
          </Stack>
        </Grid>

        <Grid xs={12} md={7}>
          <Stack gap={3}>
            <Stack direction="row" gap={2}>
              {user.personalId && (
                <FormControl>
                  <FormLabel>
                    <Trans id="Email" />
                  </FormLabel>
                  <Input {...register('email')} type="email" required fullWidth />
                </FormControl>
              )}
              {!user.personalId && (
                <FormControl>
                  <FormLabel>
                    <Trans id="Name" />
                  </FormLabel>
                  <Input {...register('name')} fullWidth />
                </FormControl>
              )}
              <FormControl>
                <FormLabel>
                  <Trans id="Phone" />
                </FormLabel>
                <Input {...register('phone')} fullWidth />
              </FormControl>
            </Stack>

            <FormControl>
              <FormLabel>
                <Trans id="Communication Language" />
              </FormLabel>
              <RadioGroup defaultValue={user.communicationLanguage}>
                <Radio value="sv" label={<Trans id="Swedish" />} {...register('communicationLanguage')} />
                <Radio value="en" label={<Trans id="English" />} {...register('communicationLanguage')} />
              </RadioGroup>
            </FormControl>
            {showReferralFields && (
              <>
                <Controller
                  control={control}
                  name="referralSource"
                  render={({ field }) => (
                    <FormControl>
                      <FormLabel>
                        <Trans id="How did you hear about us?" />
                      </FormLabel>
                      <Grid container spacing={1} sx={{ mt: 1 }}>
                        {REFERRAL_SOURCES.map(({ value, label }) => (
                          <Grid sm={6} key={value}>
                            <Card
                              variant="outlined"
                              sx={{
                                p: 1.5,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: '2.5em',
                                backgroundColor: field.value === value ? 'neutral.plainActiveBg' : undefined,
                                '&:hover': {
                                  borderColor: 'primary.outlinedBorder',
                                },
                              }}
                              onClick={() => {
                                field.onChange(value);
                              }}
                            >
                              {label}
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </FormControl>
                  )}
                />
                {referralSource === GqlReferralSource.Other && (
                  <FormControl>
                    <FormLabel>
                      <Trans id="Please specify" />
                    </FormLabel>
                    <Input {...register('referralSourceOther')} fullWidth />
                  </FormControl>
                )}
              </>
            )}
          </Stack>
        </Grid>

        {/* Footer */}
        <Grid xs={12}>
          {showError && <GenericError />}
          <Stack direction="row" justifyContent="end" sx={{ mt: 2 }}>
            <Button loading={userDetailsLoading} type="submit">
              <Trans id="Save" />
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export const UserProfileEdit = (props: ComponentProps<typeof UserProfileEditInner>) => {
  return (
    <Suspense fallback={<Box minHeight={382} minWidth={600} />}>
      <UserProfileEditInner {...props} />
    </Suspense>
  );
};
